/**
 * The console's one reading of "needs you" (ADR 0056 decision 6): the protocol's `needsYou`
 * selector over the live store (sessions, approvals, assistant steps, dismissed and snoozed
 * asks), computed once per change and shared by every surface: the drawer, Overview, the Inbox,
 * the rail and phone badges, the Sessions page, the notification centre and the palette.
 */
import { useEffect, useMemo } from 'react';
import { asksStateOf, needsYou } from '@orgus/protocol';
import type { ApprovalsState, AssistantState, Id, NeedsYou, NeedsYouSessionLevel, Session } from '@orgus/protocol';
import { useNow } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useApprovals } from '../approvals/live.ts';
import { useAssistantPending } from '../assistant/data.ts';
import { mockAsks } from './mock.ts';

/** How often the 24 hour line and snooze times are re-checked with nothing else changing. */
const TICK_MS = 60_000;

type Inputs = [Record<Id, Session>, ApprovalsState | null | undefined, unknown, AssistantState['pending'], number];

let lastIn: Inputs | null = null;
let lastOut: NeedsYou | null = null;

/** The selector over the store's slices, memoised on their identities and the minute. */
export function needsYouOf(sessions: Record<Id, Session>, approvals: ApprovalsState | null | undefined, asks: unknown, steps: AssistantState['pending'], now: number): NeedsYou {
  const ins: Inputs = [sessions, approvals, asks, steps, Math.floor(now / TICK_MS)];
  if (lastIn && lastOut && ins.every((x, i) => x === lastIn![i])) return lastOut;
  lastIn = ins;
  lastOut = needsYou({
    sessions: Object.values(sessions),
    pending: approvals?.pending ?? [],
    blocking: approvals?.blocking ?? [],
    steps: steps.map((p) => ({ id: p.step.approval?.id ?? p.entryId, title: p.step.title, at: p.at })),
    setAside: asksStateOf({ asks }).setAside,
    ...(approvals?.answerer ? { answerer: approvals.answerer.answerer } : {}),
    now,
  });
  return lastOut;
}

export function useNeedsYouState(): NeedsYou {
  useMockAsks();
  const { approvals } = useApprovals();
  const steps = useAssistantPending();
  const sessions = useOrgus((s) => s.sessions);
  const asks = useOrgus((s) => s.ext.asks);
  const now = useNow(TICK_MS);
  return useMemo(() => needsYouOf(sessions, approvals, asks, steps, now), [sessions, approvals, asks, steps, now]);
}

/** In demo mode, fill state.ext.asks with the demo dismissal once. */
export function useMockAsks(): void {
  const seed = useOrgus((s) => s.mode === 'mock' && s.phase === 'ready' && !s.ext.asks);
  useEffect(() => {
    if (seed) useOrgus.getState().applyExt({ ext: 'asks', type: 'state', data: mockAsks(Date.now()) });
  }, [seed]);
}

/** One session's place in Needs you (null: nothing waits on a person). */
export function useSessionNeedsLevel(id: Id): NeedsYouSessionLevel | null {
  return useNeedsYouState().bySession.get(id) ?? null;
}
